import React from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card } from './ui';

const DIMS = [
  ['career_direction', 'Career Direction', '#0b1f4d'],
  ['profile_strength', 'Profile Strength', '#14b8a6'],
  ['interview_readiness', 'Interview Readiness', '#f59e0b'],
  ['job_search', 'Job Search', '#0ea5e9'],
];
const fmt = (at) => (at ? new Date(at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—');

const Delta = ({ now, prev }) => {
  if (prev == null) return null;
  const d = now - prev;
  if (d === 0) return <span className="text-slate-400 ml-1">±0</span>;
  return <span className={`ml-1 ${d > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{d > 0 ? `+${d}` : d}</span>;
};

export const ReadinessTrend = ({ history }) => {
  const rows = history.map((h, i) => ({ ...h.scores, label: `#${i + 1} · ${fmt(h.at)}` }));
  return (
    <Card className="lg:col-span-2" data-testid="readiness-trend">
      <p className="font-outfit font-semibold text-[#0b1f4d]">Career Readiness over time</p>
      <p className="text-xs text-slate-500 mb-4">Every Career Readiness Check you've taken, scored out of 100.</p>
      {history.length === 0 ? <p className="text-sm text-slate-500" data-testid="readiness-trend-empty">No Readiness Check results yet.</p> : (
        <>
          {history.length > 1 && (
            <div className="h-56" data-testid="readiness-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rows} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {DIMS.map(([k, l, c]) => <Line key={k} type="monotone" dataKey={k} name={l} stroke={c} strokeWidth={2} dot={{ r: 3 }} />)}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm" data-testid="readiness-attempts-table">
              <thead><tr className="text-left text-xs text-slate-500"><th className="py-2 pr-3">Attempt</th>{DIMS.map(([k, l]) => <th key={k} className="py-2 pr-3">{l}</th>)}</tr></thead>
              <tbody>{[...history].reverse().map((h, ri) => {
                const i = history.length - 1 - ri;
                const prev = history[i - 1]?.scores;
                return (
                  <tr key={i} className="border-t border-slate-100" data-testid={`readiness-attempt-${i + 1}`}>
                    <td className="py-2 pr-3 whitespace-nowrap">#{i + 1} · {fmt(h.at)}{i === history.length - 1 && <span className="ml-2 text-[10px] uppercase text-teal-700">latest</span>}</td>
                    {DIMS.map(([k]) => <td key={k} className="py-2 pr-3 font-semibold text-[#0b1f4d]">{h.scores[k] ?? '—'}<Delta now={h.scores[k]} prev={prev?.[k]} /></td>)}
                  </tr>);
              })}</tbody>
            </table>
          </div>
          <a href="/career-readiness-check" className="inline-block mt-4 text-sm font-semibold text-teal-700" data-testid="readiness-retake-link">Retake the Readiness Check →</a>
        </>
      )}
    </Card>
  );
};
